import { useEffect, useRef, useState } from "react";
import { Loader2, X, LayoutList } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function AddToScenarioDialog({ offer, onClose }) {
  const [scenarios, setScenarios] = useState(null);
  const [mode, setMode] = useState("new");
  const [sid, setSid] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const interacted = useRef(false);

  useEffect(() => {
    api.get("/scenarios").then(({ data }) => {
      const drafts = data.filter((s) => s.status === "draft");
      setScenarios(drafts);
      if (drafts.length > 0) {
        setSid((cur) => cur || drafts[0].id);
        if (!interacted.current) setMode("existing");
      }
    }).catch(() => setScenarios([]));
  }, []);

  const route = offer.slices?.[0] ? `${offer.slices[0].origin} → ${offer.slices[0].destination}` : "Scenario";

  const save = async () => {
    setBusy(true);
    try {
      let target = sid;
      let targetName;
      if (mode === "new") {
        const { data } = await api.post("/scenarios", { name: name.trim() || route, items: [] });
        target = data.id;
        targetName = data.name;
      } else {
        targetName = scenarios.find((s) => s.id === target)?.name;
      }
      await api.post(`/scenarios/${target}/items/from-offer`, { offer });
      toast.success(`Flight added to scenario "${targetName}" — open the Planner to compare`);
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to scenario");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-surface border border-sand-300 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-to-scenario-dialog">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="label-tag mb-1">Add to scenario</p>
            <p className="text-sm text-sand-600">Locks this offer's price and times into a scenario on the Planner.</p>
          </div>
          <button onClick={() => onClose(false)} className="text-sand-400 hover:text-sand-900" data-testid="add-to-scenario-close"><X size={16} /></button>
        </div>
        {scenarios === null ? (
          <p className="text-sm text-sand-500 py-4">Loading scenarios…</p>
        ) : (
          <div className="space-y-3">
            {scenarios.length > 0 && (
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="radio" checked={mode === "existing"} onChange={() => { interacted.current = true; setMode("existing"); }} className="mt-1 accent-ember" data-testid="scenario-mode-existing" />
                <span className="flex-1">
                  <span className="block text-sm font-medium mb-1.5">Existing scenario</span>
                  <select value={sid} onChange={(e) => { interacted.current = true; setSid(e.target.value); setMode("existing"); }}
                    className="w-full border border-sand-300 bg-surface px-3 py-2 text-sm focus:outline-none focus:border-ember"
                    data-testid="scenario-select">
                    {scenarios.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.totals?.start_date ? ` · ${s.totals.start_date}` : ""}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
            )}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="radio" checked={mode === "new"} onChange={() => { interacted.current = true; setMode("new"); }} className="mt-1 accent-ember" data-testid="scenario-mode-new" />
              <span className="flex-1">
                <span className="block text-sm font-medium mb-1.5">New scenario</span>
                <input value={name} onChange={(e) => { interacted.current = true; setName(e.target.value); setMode("new"); }}
                  placeholder={route}
                  className="w-full border border-sand-300 bg-surface px-3 py-2 text-sm focus:outline-none focus:border-ember"
                  data-testid="scenario-new-name" />
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => onClose(false)} className="btn-swiss btn-ghost text-sm">Cancel</button>
              <button onClick={save} disabled={busy || (mode === "existing" && !sid)}
                className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid="add-to-scenario-confirm">
                {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <LayoutList size={14} className="mr-1.5" />}
                {busy ? "Adding…" : "Add flight"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
